import store from "@/store";
import Vue from "vue";
import VueRouter from "vue-router";

import {
    BUSINESS_NAME,
    ENABLED_AUTH,
    FRANCHISE_PORTAL_TITLE,
    LOGIN_PAGE_TITLE,
    SITE_BASE_PATH
} from "@/common/constants";

import Callback from "../pages/callback.vue";
import Login from "../pages/login.vue";
import NotFound from "../pages/not-found.vue";

Vue.use(VueRouter);

function redirectToBuilder(route = { name: "AppIndex" }) {
    return function (_to, _from, next) {
        return next(route);
    };
}

function noRequiresAuthMiddleware(_to, _from, next) {
    return next();
}

function requiresAuthMiddleware(to, _from, next) {
    if (store.state.auth.accessToken) {
        return next();
    }

    store.commit("commons/SET_POST_LOGIN_REDIRECT", to.fullPath);
    return next({ name: "Login" });
}

function requiresUnauthenticatedMiddleware(_to, _from, next) {
    if (!store.state.auth.accessToken) {
        return next();
    }
    return next({ name: "AppIndex" });
}

const buildRequiresAuthenticated = (enableAuth) => {
    if (enableAuth) {
        return requiresAuthMiddleware;
    }
    return noRequiresAuthMiddleware;
};

const buildRequiresUnauthenticated = (enableAuth) => {
    if (enableAuth) {
        return requiresUnauthenticatedMiddleware;
    }
    return redirectToBuilder({ name: "AppIndex" });
};

const validateRequiresAuthenticated = buildRequiresAuthenticated(ENABLED_AUTH);

const validateRequiresUnauthenticated =
    buildRequiresUnauthenticated(ENABLED_AUTH);

const routes = [
    {
        path: "/",
        name: "IndexLogin",
        component: Login,
        beforeEnter: validateRequiresUnauthenticated
    },
    {
        path: "/callback",
        name: "Callback",
        component: Callback
    },
    {
        path: "/wiki",
        name: "WikiIndex",
        component: () => import("../pages/wiki/index.vue")
    },
    {
        path: "/wiki/:slug",
        name: "WikiTopic",
        component: () => import("../pages/wiki/topic.vue")
    },
    {
        path: "/login",
        name: "Login",
        component: Login,
        beforeEnter: validateRequiresUnauthenticated,
        meta: {
            pageTitle: LOGIN_PAGE_TITLE
        }
    },
    {
        path: "/expired",
        name: "SessionExpired",
        component: () => import("../pages/expired.vue"),
        beforeEnter: validateRequiresUnauthenticated,
        meta: {
            pageTitle: "Sesión expirada"
        }
    },
    {
        path: "/error",
        name: "Error",
        component: Error,
        beforeEnter: validateRequiresUnauthenticated,
        meta: {
            pageTitle: "Error en api"
        }
    },
    {
        path: "/onboarding",
        component: () => import("../layouts/empty.vue"),
        children: [
            {
                path: "multitenant",
                component: () =>
                    import("../layouts/onboarding-multitenant.vue"),
                children: [
                    {
                        path: "",
                        name: "OnboardingMultitenantSlug",
                        beforeEnter: validateRequiresAuthenticated,
                        component: () =>
                            import("../pages/onboarding/multitenant/index.vue"),
                        meta: {
                            pageTitle: "Onboarding - Nombre",
                            stepKey: "slug"
                        }
                    },
                    {
                        path: "business-type",
                        name: "OnboardingMultitenantBusinessType",
                        beforeEnter: validateRequiresAuthenticated,
                        component: () =>
                            import(
                                "../pages/onboarding/multitenant/business-type.vue"
                            ),
                        meta: {
                            pageTitle: "Onboarding - Tipo de negocio",
                            stepKey: "business-type"
                        }
                    },
                    {
                        path: "branding",
                        name: "OnboardingMultitenantBranding",
                        beforeEnter: validateRequiresAuthenticated,
                        component: () =>
                            import(
                                "../pages/onboarding/multitenant/branding.vue"
                            ),
                        meta: {
                            pageTitle: "Onboarding - Marca",
                            stepKey: "branding"
                        }
                    },
                    {
                        path: "social-networks",
                        name: "OnboardingMultitenantSocialNetworks",
                        beforeEnter: validateRequiresAuthenticated,
                        component: () =>
                            import(
                                "../pages/onboarding/multitenant/social-networks.vue"
                            ),
                        meta: {
                            pageTitle: "Onboarding - Redes sociales",
                            stepKey: "social-networks"
                        }
                    },
                    {
                        path: "products",
                        name: "OnboardingMultitenantProducts",
                        beforeEnter: validateRequiresAuthenticated,
                        component: () =>
                            import(
                                "../pages/onboarding/multitenant/products.vue"
                            ),
                        meta: {
                            pageTitle: "Onboarding - Productos",
                            stepKey: "products"
                        }
                    },
                    {
                        path: "custom-links",
                        name: "OnboardingMultitenantCustomLinks",
                        beforeEnter: validateRequiresAuthenticated,
                        component: () =>
                            import(
                                "../pages/onboarding/multitenant/custom-links.vue"
                            ),
                        meta: {
                            pageTitle: "Onboarding - Enlaces",
                            stepKey: "custom-links"
                        }
                    },
                    {
                        path: "plan",
                        name: "OnboardingMultitenantPlan",
                        beforeEnter: validateRequiresAuthenticated,
                        component: () =>
                            import("../pages/onboarding/multitenant/plan.vue"),
                        meta: {
                            pageTitle: "Onboarding - Plan",
                            stepKey: "plan"
                        }
                    },
                    {
                        path: "welcome",
                        name: "OnboardingMultitenantWelcome",
                        beforeEnter: validateRequiresAuthenticated,
                        component: () =>
                            import(
                                "../pages/onboarding/multitenant/welcome.vue"
                            ),
                        meta: {
                            pageTitle: "Onboarding - Bienvenida"
                        }
                    }
                ]
            },
            {
                // Destino de la pasarela para los pagos de plataforma con
                // origen onboarding; los de Mi plan vuelven a
                // /app/plan/payment-result/:invoiceId.
                path: "payment-result/:invoiceId",
                name: "PlatformPaymentResult",
                beforeEnter: validateRequiresAuthenticated,
                component: () =>
                    import("../pages/onboarding/payment-result.vue"),
                meta: {
                    pageTitle: "Confirmando pago"
                }
            },
            {
                path: "marketplace",
                component: () => import("../layouts/onboarding.vue"),
                children: [
                    {
                        path: "",
                        name: "OnboardingMarketplaceIndex",
                        beforeEnter: validateRequiresAuthenticated,
                        component: () =>
                            import("../pages/onboarding/marketplace/index.vue"),
                        meta: {
                            pageTitle: "Onboarding"
                        }
                    },
                    {
                        path: "finish",
                        name: "OnboardingFinish",
                        beforeEnter: validateRequiresAuthenticated,
                        component: () =>
                            import(
                                "../pages/onboarding/marketplace/finish.vue"
                            ),
                        meta: {
                            pageTitle: "Onboarding finalizado"
                        }
                    },
                    {
                        path: "store",
                        name: "OnboardingNewStore",
                        beforeEnter: validateRequiresAuthenticated,
                        component: () =>
                            import("../pages/app/stores/reduced.vue"),
                        meta: {
                            pageTitle: "Crea tu tienda"
                        }
                    },
                    {
                        path: "inventories",
                        name: "OnboardingInventories",
                        beforeEnter: validateRequiresAuthenticated,
                        component: () =>
                            import("../pages/app/inventories/new.vue"),
                        meta: {
                            pageTitle: "Registra inventario"
                        }
                    },
                    {
                        path: "order",
                        name: "OnboardingNewOrder",
                        beforeEnter: validateRequiresAuthenticated,
                        component: () => import("../pages/app/orders/new.vue"),
                        meta: {
                            pageTitle: "Primera venta"
                        }
                    }
                ]
            }
        ]
    },
    {
        path: "/app/customization",
        component: () => import("../layouts/full-screen.vue"),
        children: [
            {
                path: "editor",
                name: "CatalogEditor",
                component: () =>
                    import("../pages/app/customization/editor.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Editor de catálogo"
                }
            }
        ]
    },

    {
        path: "/app",
        component: () => import("../layouts/app.vue"),
        children: [
            {
                path: "",
                name: "AppIndex",
                beforeEnter: validateRequiresAuthenticated,
                component: () => import("../pages/app/index.vue"),
                meta: {
                    pageTitle: `${BUSINESS_NAME} Portal`
                }
            },

            {
                path: "banners",
                name: "BannersIndex",
                component: () => import("../pages/app/banners/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Listado de banners"
                }
            },
            {
                path: "banners/new",
                name: "BannersNew",
                component: () => import("../pages/app/banners/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Nuevo banner"
                }
            },
            {
                path: "banners/detail/:id",
                name: "BannersDetail",
                component: () => import("../pages/app/banners/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Detalle de banner"
                }
            },

            {
                path: "blogs",
                name: "BlogsIndex",
                component: () => import("../pages/app/blogs/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Páginas personalizadas"
                }
            },
            {
                path: "blogs/new",
                name: "BlogsNew",
                component: () => import("../pages/app/blogs/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Nueva página personalizada"
                }
            },
            {
                path: "blogs/detail/:slug",
                name: "BlogsDetail",
                component: () => import("../pages/app/blogs/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Detalle de página personalizada"
                }
            },
            {
                path: "blogs/clone/:slugClone",
                name: "BlogsClone",
                component: () => import("../pages/app/blogs/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Clonar página personalizada"
                }
            },

            {
                path: "brands",
                name: "BrandsIndex",
                component: () => import("../pages/app/brands/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Listado de marcas"
                }
            },
            {
                path: "brands/highlight",
                name: "BrandsHighlight",
                component: () => import("../pages/app/brands/highlight.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Marcas destacadas"
                }
            },
            {
                path: "brands/new",
                name: "BrandsNew",
                component: () => import("../pages/app/brands/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Registrar marca"
                }
            },
            {
                path: "brands/detail/:slug",
                name: "BrandsDetail",
                component: () => import("../pages/app/brands/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Detalle de marca"
                }
            },
            {
                path: "brands/clone/:slugClone",
                name: "BrandsClone",
                component: () => import("../pages/app/brands/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Clonar marca"
                }
            },

            {
                path: "carts",
                name: "cartsIndex",
                component: () => import("../pages/app/carts/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Carros de compras"
                }
            },
            {
                path: "carts/new",
                name: "cartsNew",
                component: () => import("../pages/app/carts/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Registro de carro de compras"
                }
            },
            {
                path: "carts/detail/:id",
                name: "CartDetail",
                component: () => import("../pages/app/carts/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Detalle de carro de compras"
                }
            },
            {
                path: "carts/clone/:idClone",
                name: "CartClone",
                component: () => import("../pages/app/carts/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Clonar carro de compras"
                }
            },

            {
                path: "cash-outputs",
                name: "CashOutputs",
                component: () => import("../pages/app/cash-outputs/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Gastos",
                    usesMerchantContext: true
                }
            },
            {
                path: "cash-outputs/new",
                name: "CashOutputNew",
                component: () => import("../pages/app/cash-outputs/new.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Registrar gasto",
                    usesMerchantContext: true
                }
            },
            {
                path: "cash-outputs/detail/:id",
                name: "CashOutputDetail",
                component: () => import("../pages/app/cash-outputs/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Detalle de gasto",
                    usesMerchantContext: true
                }
            },

            {
                path: "collections",
                name: "CollectionsIndex",
                component: () => import("../pages/app/collections/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Listado de colecciones"
                }
            },
            {
                path: "collections/new",
                name: "CollectionsNew",
                component: () => import("../pages/app/collections/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Nueva colección"
                }
            },
            {
                path: "collections/highlight",
                name: "CollectionsHighlight",
                component: () =>
                    import("../pages/app/collections/highlight.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Colecciones destacadas"
                }
            },
            {
                path: "collections/detail/:slug",
                name: "CollectionsDetail",
                component: () => import("../pages/app/collections/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Detalle de colección"
                }
            },
            {
                path: "collections/clone/:slugClone",
                name: "CollectionsClone",
                component: () => import("../pages/app/collections/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Clonar colección"
                }
            },

            {
                path: "configurations",
                name: "Configurations",
                beforeEnter: validateRequiresAuthenticated,
                component: () => import("../pages/app/configurations.vue"),
                meta: {
                    pageTitle: "Configuraciones"
                }
            },

            {
                path: "custom-links",
                name: "CustomLinksIndex",
                component: () => import("../pages/app/custom-links/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Enlaces personalizados"
                }
            },

            {
                path: "customers",
                name: "CustomersIndex",
                component: () => import("../pages/app/customers/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Clientes"
                }
            },
            {
                path: "customers/new",
                name: "CustomersNew",
                component: () => import("../pages/app/customers/new.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Registrar cliente"
                }
            },
            {
                path: "customers/detail/:id",
                name: "CustomersDetail",
                component: () => import("../pages/app/customers/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Detalles de cliente"
                }
            },

            {
                path: "employees",
                name: "EmployeesIndex",
                component: () => import("../pages/app/employees/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Colaboradores",
                    usesMerchantContext: true
                }
            },

            {
                path: "files",
                name: "FilesIndex",
                component: () => import("../pages/app/files/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Listado de archivos"
                }
            },
            {
                path: "files/new",
                name: "FilesNew",
                component: () => import("../pages/app/files/new.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Nuevo archivo"
                }
            },
            {
                path: "files/detail/:id",
                name: "FilesDetail",
                component: () => import("../pages/app/files/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Detalle de archivo"
                }
            },

            {
                path: "images",
                name: "ImagesIndex",
                component: () => import("../pages/app/images/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Listado de imágenes"
                }
            },
            {
                path: "images/new",
                name: "ImagesNew",
                component: () => import("../pages/app/images/new.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Nueva imagen"
                }
            },
            {
                path: "images/detail/:id",
                name: "ImagesDetail",
                component: () => import("../pages/app/images/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Detalle de imagen"
                }
            },

            {
                path: "inventories",
                name: "InventoriesIndex",
                component: () => import("../pages/app/inventories/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Productos en inventario",
                    usesMerchantContext: true
                }
            },
            {
                path: "inventories/history",
                name: "InventoriesHistory",
                component: () => import("../pages/app/inventories/history.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Histórico de inventario",
                    usesMerchantContext: true
                }
            },
            {
                path: "inventories/input-detail/:id",
                name: "InventoriesInputDetail",
                component: () =>
                    import("../pages/app/inventories/input-detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Detalle de entrada",
                    usesMerchantContext: true
                }
            },

            {
                path: "inventories/massive",
                name: "InventoriesMassiveIndex",
                component: () =>
                    import("../pages/app/inventories/massive/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Carga masiva de inventario",
                    usesMerchantContext: true
                }
            },
            {
                path: "inventories/massive/load",
                name: "InventoriesMassiveLoad",
                component: () =>
                    import("../pages/app/inventories/massive/load.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Carga masiva de inventario",
                    usesMerchantContext: true
                }
            },
            {
                path: "inventories/massive/load-and-updates",
                name: "InventoriesMassiveLoadAndUpdates",
                component: () =>
                    import("../pages/app/inventories/massive/load.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Carga y actualización masiva de inventario",
                    usesMerchantContext: true
                }
            },
            {
                path: "inventories/new",
                name: "InventoriesNew",
                component: () => import("../pages/app/inventories/new.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Registro de inventario",
                    usesMerchantContext: true
                }
            },
            {
                path: "inventories/detail/:id",
                name: "InventoriesDetail",
                component: () => import("../pages/app/inventories/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Detalle de inventario",
                    usesMerchantContext: true
                }
            },
            {
                path: "inventories/:merchantType/:merchantId/detail/:id",
                name: "InventoriesCustomDetail",
                component: () => import("../pages/app/inventories/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Detalle de inventario",
                    usesMerchantContext: true
                }
            },

            {
                path: "lines",
                name: "LinesIndex",
                component: () => import("../pages/app/lines/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Listado de categorías"
                }
            },
            {
                path: "lines/new",
                name: "LinesNew",
                component: () => import("../pages/app/lines/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Registrar categoría"
                }
            },
            {
                path: "lines/detail/:slug",
                name: "LinesDetail",
                component: () => import("../pages/app/lines/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Detalles de categoría"
                }
            },
            {
                path: "lines/clone/:slugClone",
                name: "LinesClone",
                component: () => import("../pages/app/lines/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Clonar categoría"
                }
            },

            {
                path: "logistic-configurations",
                name: "SupplyChainsIndex",
                component: () =>
                    import("../pages/app/logistic-configurations/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Configuraciones logísticas"
                }
            },
            {
                path: "logistic-configurations/new",
                name: "LogisticConfigurationNew",
                component: () =>
                    import("../pages/app/logistic-configurations/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Nueva configuración logística"
                }
            },
            {
                path: "logistic-configurations/detail/:slug",
                name: "LogisticConfigurationDetail",
                component: () =>
                    import("../pages/app/logistic-configurations/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                props: true,
                meta: {
                    pageTitle: "Detalles de configuracíon logistica"
                }
            },
            {
                path: "logistic-configurations/clone/:idClone",
                name: "LogisticConfigurationsClone",
                component: () =>
                    import("../pages/app/logistic-configurations/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                props: true,
                meta: {
                    pageTitle: "Detalles de configuracíon logistica"
                }
            },

            {
                path: "orders",
                name: "OrdersIndex",
                component: () => import("../pages/app/orders/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Ordenes"
                }
            },
            {
                path: "orders/hub",
                name: "OrdersHub",
                component: () => import("../pages/app/orders/hub.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Nueva Venta"
                }
            },
            {
                path: "orders/references",
                name: "OrdersReferences",
                component: () => import("../pages/app/orders/references.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Más ordenadas"
                }
            },
            {
                path: "orders/schedule",
                name: "OrdersSchedule",
                component: () => import("../pages/app/orders/schedule.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Calendario de entregas"
                }
            },
            {
                path: "orders/refactor/:id",
                name: "OrdersRefactor",
                component: () => import("../pages/app/orders/refactor.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Refactorizar orden"
                }
            },
            {
                path: "orders/new",
                name: "OrderNew",
                component: () => import("../pages/app/orders/new.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Orden Ecommerce"
                }
            },
            {
                path: "orders/record",
                name: "RecordOrder",
                component: () => import("../pages/app/orders/record.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Venta Rápida"
                }
            },
            {
                path: "orders/new-in-merchant",
                name: "OrderNewInMerchant",
                component: () => import("../pages/app/orders/new.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Venta En Tienda",
                    usesMerchantContext: true
                }
            },
            {
                path: "orders/product",
                name: "SalesProduct",
                component: () => import("../pages/app/orders/product.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Productos por despachar"
                }
            },
            {
                path: "orders/product/:idSku",
                name: "OrdersProductId",
                component: () => import("../pages/app/orders/product.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Ventas por producto"
                }
            },
            {
                path: "orders/detail",
                name: "OrdersSearchDetail",
                component: () => import("../pages/app/orders/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Detalle de orden"
                }
            },
            {
                path: "orders/detail/:id",
                name: "OrdersDetail",
                component: () => import("../pages/app/orders/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: (route) => `Detalle de orden #${route.params.id}`
                }
            },
            {
                path: "orders/:merchantType/:merchantId/detail/:id",
                name: "OrdersReducedDetail",
                component: () => import("../pages/app/orders/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: (route) => `Detalle de orden #${route.params.id}`
                }
            },

            {
                path: "orders-groups",
                name: "OrdersGroupsIndex",
                component: () => import("../pages/app/orders-groups/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Agrupación de ordenes"
                }
            },
            {
                path: "orders-groups/detail/:id",
                name: "OrdersGroupsDetail",
                component: () =>
                    import("../pages/app/orders-groups/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                props: true,
                meta: {
                    pageTitle: "Detalle agrupación de orden"
                }
            },

            {
                path: "plan",
                name: "PlanIndex",
                component: () => import("../pages/app/plan/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Mi plan"
                }
            },
            {
                path: "plan/change",
                name: "PlanChange",
                component: () => import("../pages/app/plan/change.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Cambiar de plan"
                }
            },
            {
                path: "plan/payment-result/:invoiceId",
                name: "MyPlanPaymentResult",
                component: () => import("../pages/app/plan/payment-result.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Confirmando pago"
                }
            },
            {
                path: "settings/features",
                name: "SettingsFeatures",
                component: () => import("../pages/app/plan/features.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Funciones de tu negocio"
                }
            },

            {
                path: "profile",
                name: "Profile",
                beforeEnter: validateRequiresAuthenticated,
                component: () => import("../pages/app/profile.vue"),
                meta: {
                    pageTitle: "Perfil"
                }
            },

            {
                path: "products",
                name: "ProductsIndex",
                component: () => import("../pages/app/products/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Listado de productos"
                }
            },
            {
                path: "products/new",
                name: "ProductsNew",
                component: () => import("../pages/app/products/new/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Nuevo producto"
                }
            },
            {
                path: "products/new/fast",
                name: "ProductsFastCreate",
                component: () => import("../pages/app/products/new/fast.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Creación rápida"
                }
            },
            {
                path: "products/new/complete",
                name: "ProductsCompleteCreate",
                component: () =>
                    import("../pages/app/products/new/complete.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Creación completa"
                }
            },
            {
                path: "products/highlight",
                name: "ReferencesHighlight",
                component: () => import("../pages/app/products/highlight.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Productos destacados"
                }
            },
            {
                path: "products/massive",
                name: "ProductsLoad",
                component: () => import("../pages/app/products/massive/index"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Carga masiva de productos"
                }
            },
            {
                path: "products/massive/load",
                name: "ProductsMassiveLoad",
                component: () =>
                    import("../pages/app/products/massive/load.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Carga masiva de productos"
                }
            },
            {
                path: "products/massive/load-and-updates",
                name: "ProductsMassiveLoadAndUpdates",
                component: () =>
                    import("../pages/app/products/massive/load.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle:
                        "Carga y actualización masiva de productos y referencias"
                }
            },
            {
                path: "products/massive/load-with-inventories",
                name: "ProductsAndInventoriesMassiveLoad",
                component: () =>
                    import(
                        "../pages/app/products/massive/load-with-inventories.vue"
                    ),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Carga masiva de productos e inventario"
                }
            },
            {
                path: "products/massive/update-prices",
                name: "ProductsMassiveUpdatePrices",
                component: () =>
                    import("../pages/app/products/massive/update-prices.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Actualización masiva de precios"
                }
            },
            {
                path: "products/detail/:id",
                name: "ProductsDetail",
                component: () => import("../pages/app/products/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                props: true,
                meta: {
                    pageTitle: (route) =>
                        `Detalle de producto #${route.params.id}`
                }
            },
            {
                path: "products/clone/:idClone",
                name: "ProductsClone",
                component: () => import("../pages/app/products/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Clonar producto"
                }
            },

            {
                path: "roles",
                name: "ListRoles",
                component: () => import("../pages/app/roles/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Lista de roles"
                }
            },
            {
                path: "roles/detail/:slug",
                name: "RolesDetail",
                component: () => import("../pages/app/roles/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Detalle de roles"
                }
            },

            {
                path: "merchant/services",
                name: "MerchantServices",
                component: () => import("../pages/app/merchant/services.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Servicios",
                    usesMerchantContext: true
                }
            },
            {
                path: "merchant/box",
                name: "MerchantBox",
                component: () => import("../pages/app/merchant/box.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Caja",
                    usesMerchantContext: true
                }
            },

            {
                path: "stores",
                name: "StoresIndex",
                component: () => import("../pages/app/stores/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Listado de tiendas"
                }
            },
            {
                path: "stores/new",
                name: "StoreNew",
                component: () => import("../pages/app/stores/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Nueva tienda"
                }
            },
            {
                path: "stores/detail/:id",
                name: "StoresDetail",
                component: () => import("../pages/app/stores/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                props: true,
                meta: {
                    pageTitle: "Detalle de tienda"
                }
            },
            {
                path: "stores/clone/:idClone",
                name: "StoresClone",
                component: () => import("../pages/app/stores/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Clonar tienda"
                }
            },
            {
                path: "stores/record",
                name: "StoreCreate",
                component: () => import("../pages/app/stores/reduced.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Registrar tienda"
                }
            },
            {
                path: "stores/reduced/:id",
                name: "StoresReduced",
                component: () => import("../pages/app/stores/reduced.vue"),
                beforeEnter: validateRequiresAuthenticated,
                props: true,
                meta: {
                    pageTitle: "Detalle de tienda"
                }
            },
            {
                path: "sublines",
                redirect: (to) => ({
                    path: "/app/lines",
                    query: { ...to.query, type: "subline" }
                })
            },
            {
                path: "sublines/new",
                redirect: "/app/lines/new"
            },
            {
                path: "sublines/detail/:slug",
                redirect: (to) => `/app/lines/detail/${to.params.slug}`
            },
            {
                path: "sublines/clone/:slugClone",
                redirect: (to) => `/app/lines/clone/${to.params.slugClone}`
            },
            {
                path: "suppliers",
                name: "SuppliersIndex",
                component: () => import("../pages/app/suppliers/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Listado de proveedores"
                }
            },
            {
                path: "suppliers/new",
                name: "SupplierNew",
                component: () => import("../pages/app/suppliers/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Nuevo proveedor"
                }
            },
            {
                path: "suppliers/detail/:id",
                name: "SuppliersDetail",
                component: () => import("../pages/app/suppliers/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Detalles de proveedor"
                }
            },
            {
                path: "suppliers/clone/:idClone",
                name: "SuppliersClone",
                component: () => import("../pages/app/suppliers/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Clonar proveedor"
                }
            },

            {
                path: "supply-lists",
                name: "SupplyListIndex",
                component: () => import("../pages/app/supply-lists/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Listas de compras",
                    usesMerchantContext: true
                }
            },
            {
                path: "supply-lists/new",
                name: "SupplyListNew",
                component: () => import("../pages/app/supply-lists/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Nueva lista de compras",
                    usesMerchantContext: true
                }
            },
            {
                path: "supply-lists/detail/:id",
                name: "SupplyListDetail",
                component: () => import("../pages/app/supply-lists/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                props: true,
                meta: {
                    pageTitle: "Detalles de lista de compras",
                    usesMerchantContext: true
                }
            },
            {
                path: "supply-lists/clone/:idClone",
                name: "SupplyListClone",
                component: () => import("../pages/app/supply-lists/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                props: true,
                meta: {
                    pageTitle: "Detalles de lista de compras",
                    usesMerchantContext: true
                }
            },

            {
                path: "users/new",
                name: "UsersNew",
                component: () => import("../pages/app/users/new.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Crear usuario"
                }
            },
            {
                path: "users",
                name: "UsersIndex",
                component: () => import("../pages/app/users/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Usuarios"
                }
            },
            {
                path: "users/detail/:id",
                name: "UsersDetail",
                component: () => import("../pages/app/users/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                props: true,
                meta: {
                    pageTitle: "Detalles de colaborador"
                }
            },

            {
                path: "customization/header",
                name: "CustomizationHeader",
                component: () =>
                    import("../pages/app/customization/header.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Perfil del negocio"
                }
            },
            {
                path: "customization/design",
                name: "CustomizationDesign",
                component: () =>
                    import("../pages/app/customization/design.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Diseño"
                }
            },

            {
                path: "settings",
                name: "SettingsIndex",
                component: () => import("../pages/app/settings/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Configuración"
                }
            },
            {
                path: "settings/catalog",
                name: "SettingsCatalog",
                component: () => import("../pages/app/settings/catalog.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Tipos de producto"
                }
            },
            {
                path: "settings/payment-methods",
                name: "SettingsPaymentMethods",
                component: () =>
                    import("../pages/app/settings/payment-methods.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Métodos de pago del portal"
                }
            },
            {
                path: "settings/delivery-schedule",
                name: "SettingsDeliverySchedule",
                component: () =>
                    import("../pages/app/settings/delivery-schedule.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Tiempos de entrega"
                }
            },
            {
                path: "settings/delivery-prices",
                name: "SettingsDeliveryPrices",
                component: () =>
                    import("../pages/app/settings/delivery-prices.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Precios de entrega"
                }
            },
            {
                path: "settings/checkout-payment-methods",
                name: "SettingsCheckoutPaymentMethods",
                component: () =>
                    import(
                        "../pages/app/settings/checkout-payment-methods.vue"
                    ),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Métodos de pago del checkout"
                }
            },
            {
                path: "settings/minimum-order-values",
                name: "SettingsMinimumOrderValues",
                component: () =>
                    import("../pages/app/settings/minimum-order-values.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Valores mínimos de orden"
                }
            },
            {
                path: "settings/inventory",
                name: "SettingsInventory",
                component: () => import("../pages/app/settings/inventory.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Inventario"
                }
            },
            {
                path: "settings/tags",
                name: "SettingsTags",
                component: () => import("../pages/app/settings/tags.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Tags"
                }
            },
            {
                path: "settings/filters",
                name: "SettingsFilters",
                component: () => import("../pages/app/settings/filters.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Filtros del catálogo"
                }
            },
            {
                path: "settings/api-keys",
                name: "SettingsApiKeys",
                component: () => import("../pages/app/settings/api-keys.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "API Keys"
                }
            },
            {
                path: "settings/orders",
                redirect: "/app/settings"
            },
            {
                path: "tenants/settings/logos",
                redirect: "/app/customization/header"
            },
            {
                path: "tenants/settings/description",
                redirect: "/app/customization/header"
            },
            {
                path: "tenants/settings/colors",
                redirect: "/app/customization/design"
            },
            {
                path: "tenants/settings/whatsapp",
                redirect: "/app/customization/header"
            },
            {
                path: "tenants/settings/social",
                redirect: "/app/customization/header"
            },
            {
                path: "tenants/editor",
                redirect: "/app/customization/editor"
            },
            {
                path: "tenants/settings/catalog",
                redirect: "/app/settings/catalog"
            },
            {
                path: "tenants/settings/payment-methods",
                redirect: "/app/settings/payment-methods"
            },
            {
                path: "tenants/settings/orders",
                redirect: "/app/settings"
            },
            {
                path: "tenants",
                name: "TenantsIndex",
                component: () => import("../pages/app/tenants/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Tenants"
                }
            },
            {
                path: "tenants/detail/:id",
                name: "TenantDetail",
                component: () => import("../pages/app/tenants/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                props: true,
                meta: {
                    pageTitle: "Detalle de tenant"
                }
            },
            {
                path: "shared-images",
                name: "SharedImagesIndex",
                component: () => import("../pages/app/shared-images/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Banco de imágenes compartidas"
                }
            },
            {
                path: "warehouses",
                name: "WarehousesIndex",
                component: () => import("../pages/app/warehouses/index.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Listado de bodega"
                }
            },
            {
                path: "warehouses/detail/:id",
                name: "WarehouseDetails",
                component: () => import("../pages/app/warehouses/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                props: true,
                meta: {
                    pageTitle: "Detalle de bodega"
                }
            },
            {
                path: "warehouses/new",
                name: "WarehouseNew",
                component: () => import("../pages/app/warehouses/detail.vue"),
                beforeEnter: validateRequiresAuthenticated,
                meta: {
                    pageTitle: "Nueva bodega"
                }
            }
        ]
    },
    {
        path: "/not-found",
        name: "NotFount",
        component: NotFound
    },
    {
        path: "*",
        redirect: "/not-found"
    }
];

const router = new VueRouter({
    mode: "history",
    base: SITE_BASE_PATH,
    routes,
    // eslint-disable-next-line no-unused-vars
    scrollBehavior(_to, _from, _savedPosition) {
        return { top: 0 };
    }
});

router.afterEach((to) => {
    store.commit("control/UPDATE_SIDEBAR_STATE", false);
    Vue.nextTick(() => {
        const title =
            typeof to.meta.pageTitle === "function"
                ? to.meta.pageTitle(to)
                : to.meta.pageTitle;
        document.title = title || FRANCHISE_PORTAL_TITLE;
    });

    const matchedCard = store.state.control.cardLinksAvailable.find(
        (item) => item.url === to.path
    );
    if (matchedCard) {
        store.commit("control/PUSH_RECENT_LINK", matchedCard);
    }
});

export default router;
